import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Table, EmptyState, Badge } from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useGetUsageChargesQuery,
  useGetUsageCostBreakdownQuery,
  useGetUsageMomQuery,
  type UsageCostBreakdownRow,
  type UsageMomSeriesRow,
} from '../../store/api/billingApi';
import { formatMoneyPreview } from '../Staff/pricingPreview';
import {
  BillHeaderRow,
  BillRow,
  BillRows,
  DimChip,
  DimensionRow,
  FeeCompare,
  FeeNow,
  FeeWas,
  Footnote,
  FreePeriodBanner,
  FreePeriodCopy,
  FreePeriodTitle,
  InsightStrip,
  KpiCard,
  KpiLabel,
  KpiStrip,
  KpiValue,
  Mono,
  PanelCard,
  PeriodLabel,
  PlanLine,
  ProgressFill,
  ProgressTrack,
  SectionTitle,
} from './Payments.styles';

type Dimension = 'application' | 'agent' | 'model';

function money(n: number | undefined | null): string {
  return formatMoneyPreview(n);
}

function formatPeriodRange(from?: string, to?: string): string | null {
  if (!from || !to) return null;
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
}

export const UsagePanel: React.FC = () => {
  const [dimension, setDimension] = useState<Dimension>('application');
  const { data: bill, isLoading: billLoading } = useGetUsageChargesQuery();
  const { data: breakdown, isLoading: breakdownLoading } =
    useGetUsageCostBreakdownQuery({ dimension });
  const { data: mom } = useGetUsageMomQuery({ months: 6 });

  const threshold = bill?.includedThresholdUsd ?? 0;
  const pre = bill?.preDiscountCostUsd ?? 0;
  const progressPct =
    threshold > 0 ? Math.min(100, (pre / threshold) * 100) : pre > 0 ? 100 : 0;
  const inUsage = bill?.tier === 'usage';
  const inFree = !!bill?.inFreePeriod;
  const notPayingYet = inFree && (bill?.totalUsd ?? 0) === 0;
  const listFee = bill?.subscriptionFeeUsd ?? bill?.subscriptionPaidUsd ?? 0;
  const periodLabel = formatPeriodRange(bill?.from, bill?.to);

  const freeUntilLabel = (() => {
    if (!bill?.freeUntil) return null;
    const d = new Date(bill.freeUntil);
    if (Number.isNaN(d.getTime())) return bill.freeUntil.slice(0, 7);
    // free_until is exclusive month start — show previous month as last free month
    const last = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1),
    );
    return last.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  })();

  const joinedLabel = (() => {
    if (!bill?.joinedAt) return null;
    const d = new Date(bill.joinedAt);
    if (Number.isNaN(d.getTime())) return bill.joinedAt.slice(0, 10);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  })();

  const insight = (() => {
    if (!bill) return 'Loading this month’s bill…';
    if (notPayingYet) {
      return (
        <>
          You’re not paying anything yet — <strong>no subscription fee</strong>
          {freeUntilLabel ? (
            <>
              {' '}
              until <strong>{freeUntilLabel}</strong>
            </>
          ) : null}
          . Usage above the included allowance still bills.
        </>
      );
    }
    if (inFree && inUsage) {
      return (
        <>
          <strong>No subscription fee</strong> this period (free subscription),
          but <strong>{money(bill.overageUsd)}</strong> above the included
          allowance is billed.
        </>
      );
    }
    if (inUsage) {
      return (
        <>
          You’ve moved into the usage tier —{' '}
          <strong>{money(bill.overageUsd)}</strong> above the included allowance
          is billed this period.
        </>
      );
    }
    return (
      <>
        You’re in the included tier —{' '}
        <strong>
          {money(bill.remainingIncludedUsd)} of {money(threshold)}
        </strong>{' '}
        plan usage left this period.
      </>
    );
  })();

  const breakdownColumns: Column<UsageCostBreakdownRow>[] = useMemo(() => {
    if (dimension === 'agent') {
      return [
        {
          key: 'label',
          header: 'Agent',
          render: (_val, row) => row.label,
        },
        {
          key: 'applicationName',
          header: 'Application',
          render: (_val, row) => row.applicationName || row.applicationId || '—',
        },
        {
          key: 'usageCostUsd',
          header: 'Usage cost',
          render: (_val, row) => <Mono>{money(row.usageCostUsd)}</Mono>,
        },
      ];
    }
    return [
      {
        key: 'label',
        header: dimension === 'application' ? 'Application' : 'Model',
        render: (_val, row) => row.label,
      },
      {
        key: 'usageCostUsd',
        header: 'Usage cost',
        render: (_val, row) => <Mono>{money(row.usageCostUsd)}</Mono>,
      },
      {
        key: 'sharePct',
        header: 'Share',
        render: (_val, row) => `${row.sharePct}%`,
      },
    ];
  }, [dimension]);

  const momColumns: Column<UsageMomSeriesRow>[] = [
    {
      key: 'month',
      header: 'Month',
      render: (_val, r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {r.month}
          {r.inFreePeriod ? (
            <Badge variant="success" size="sm">
              Free
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'subscriptionPaidUsd',
      header: 'Subscription',
      render: (_val, r) => (
        <Mono>
          {r.inFreePeriod ? (
            <span style={{ color: 'var(--accent, #0f766e)', fontWeight: 600 }}>
              {money(0)}
            </span>
          ) : (
            money(r.subscriptionPaidUsd)
          )}
        </Mono>
      ),
    },
    {
      key: 'usageChargesUsd',
      header: 'Usage charges',
      render: (_val, r) => <Mono>{money(r.usageChargesUsd)}</Mono>,
    },
    {
      key: 'totalUsd',
      header: 'Total',
      render: (_val, r) => (
        <Mono>
          <strong>{money(r.totalUsd)}</strong>
        </Mono>
      ),
    },
  ];

  const chartData = (mom?.series || []).map((r) => ({
    month: r.month,
    subscription: r.subscriptionPaidUsd,
    usage: r.usageChargesUsd,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <PanelCard padding={24}>
        <BillHeaderRow>
          <div>
            <SectionTitle style={{ margin: 0 }}>This month’s bill</SectionTitle>
            {periodLabel ? <PeriodLabel>{periodLabel}</PeriodLabel> : null}
          </div>
          {inFree ? (
            <Badge variant="success" size="sm">
              No subscription fee
            </Badge>
          ) : null}
        </BillHeaderRow>

        {inFree && (
          <FreePeriodBanner>
            <div style={{ flex: '1 1 14rem' }}>
              <FreePeriodTitle>
                {notPayingYet
                  ? 'You’re not paying anything yet'
                  : 'Subscription fee waived this period'}
              </FreePeriodTitle>
              <FreePeriodCopy>
                {notPayingYet ? (
                  <>
                    Your organisation has <strong>no subscription fee</strong>
                    {freeUntilLabel ? (
                      <>
                        {' '}
                        through <strong>{freeUntilLabel}</strong>
                      </>
                    ) : null}
                    . Included usage still applies; overage above{' '}
                    <strong>{money(threshold)}</strong> would bill separately.
                  </>
                ) : (
                  <>
                    Subscription is <strong>$0</strong>
                    {freeUntilLabel ? (
                      <>
                        {' '}
                        through <strong>{freeUntilLabel}</strong>
                      </>
                    ) : null}
                    . Usage charges below still apply.
                  </>
                )}
              </FreePeriodCopy>
              <FeeCompare>
                <FeeWas>{money(listFee)}/mo</FeeWas>
                <FeeNow>$0 subscription</FeeNow>
              </FeeCompare>
            </div>
          </FreePeriodBanner>
        )}

        {!inFree && <InsightStrip>{insight}</InsightStrip>}
        {inFree && !notPayingYet && (
          <InsightStrip $variant="free">{insight}</InsightStrip>
        )}

        {bill && (
          <>
            <PlanLine>
              {inFree ? (
                <>
                  <strong>No subscription fee</strong> this period
                  {listFee > 0 ? (
                    <> (plan list fee {money(listFee)}/mo waived)</>
                  ) : null}{' '}
                  · Includes up to <strong>{money(threshold)}</strong> of usage ·
                  Usage above that is billed separately.
                </>
              ) : (
                <>
                  <strong>
                    Subscription {money(bill.subscriptionPaidUsd)}/mo
                  </strong>{' '}
                  · Includes up to <strong>{money(threshold)}</strong> of usage
                  this period · Usage above that is billed separately.
                </>
              )}
            </PlanLine>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.875rem',
                  marginBottom: '0.35rem',
                }}
              >
                <span>
                  Usage cost this period <Mono>{money(pre)}</Mono>
                  {inUsage
                    ? ` · Included ${money(threshold)} + overage ${money(bill.overageUsd)}`
                    : ` of ${money(threshold)} included`}
                </span>
                <strong>
                  {inFree
                    ? 'Free subscription'
                    : inUsage
                      ? 'In usage tier'
                      : 'In included tier'}
                </strong>
              </div>
              <ProgressTrack>
                <ProgressFill $pct={progressPct} $danger={inUsage} />
              </ProgressTrack>
            </div>
            <BillRows>
              <BillRow $emphasis={inFree}>
                <span>
                  Subscription fee
                  {inFree ? (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                      {' '}
                      · waived
                    </span>
                  ) : null}
                </span>
                <Mono>
                  {inFree ? (
                    <>
                      <span
                        style={{
                          color: 'var(--text-muted)',
                          textDecoration: 'line-through',
                          marginRight: '0.4rem',
                          fontWeight: 400,
                        }}
                      >
                        {money(listFee)}
                      </span>
                      $0.00
                    </>
                  ) : (
                    money(bill.subscriptionPaidUsd)
                  )}
                </Mono>
              </BillRow>
              <BillRow>
                <span>
                  Usage charges
                  {inUsage ? (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                      {' '}
                      ({money(bill.overageUsd)} overage)
                    </span>
                  ) : null}
                </span>
                <Mono>{money(bill.usageChargesUsd)}</Mono>
              </BillRow>
              <BillRow $total>
                <span>Estimated total</span>
                <Mono>{money(bill.totalUsd)}</Mono>
              </BillRow>
            </BillRows>
            <Footnote>
              {inFree
                ? 'You are not charged a subscription fee during the free period. Amounts use your organisation’s rates — not provider list prices.'
                : 'Amounts use your organisation’s negotiated rates — not provider list prices. Discount %, margin %, and raw LLM cost are not shown here.'}
            </Footnote>
          </>
        )}
        {billLoading && <Text variant="body2">Loading bill…</Text>}
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Cost breakdown</SectionTitle>
        <Text variant="body2" style={{ color: 'var(--text-muted)', margin: 0 }}>
          See where usage cost went this period. Switch dimension — same totals,
          different cut. Token drivers stay on Analytics.
        </Text>
        <DimensionRow>
          {(['application', 'agent', 'model'] as Dimension[]).map((d) => (
            <DimChip
              key={d}
              type="button"
              $active={dimension === d}
              onClick={() => setDimension(d)}
            >
              By {d}
            </DimChip>
          ))}
        </DimensionRow>
        {breakdownLoading && <Text variant="body2">Loading breakdown…</Text>}
        {!breakdownLoading && (breakdown?.rows?.length ?? 0) === 0 && (
          <EmptyState
            title="No usage cost this period"
            description="Cost appears here once agents run and usage is recorded."
          />
        )}
        {(breakdown?.rows?.length ?? 0) > 0 && (
          <Table
            columns={breakdownColumns}
            data={breakdown!.rows}
            rowKey="key"
          />
        )}
        <Footnote>
          Pre-discount usage cost by dimension. For token drivers, open{' '}
          <Link to="/analytics">Analytics Center</Link>.
        </Footnote>
        <Link to="/analytics">View token drivers in Analytics →</Link>
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Month-on-month costs</SectionTitle>
        <KpiStrip>
          <KpiCard>
            <KpiLabel>This month (est.)</KpiLabel>
            <KpiValue>{money(mom?.kpis?.thisMonthUsd)}</KpiValue>
            {inFree && notPayingYet ? (
              <Footnote>No subscription fee</Footnote>
            ) : null}
          </KpiCard>
          <KpiCard>
            <KpiLabel>Trailing average</KpiLabel>
            <KpiValue>{money(mom?.kpis?.trailingAverageUsd)}</KpiValue>
          </KpiCard>
          <KpiCard>
            <KpiLabel>Highest month</KpiLabel>
            <KpiValue>{money(mom?.kpis?.highestMonthUsd)}</KpiValue>
            <Footnote>{mom?.kpis?.highestMonth || '—'}</Footnote>
          </KpiCard>
        </KpiStrip>
        {chartData.length > 0 && (
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) =>
                    money(typeof v === 'number' ? v : Number(v) || 0)
                  }
                />
                <Legend />
                <Bar
                  dataKey="subscription"
                  stackId="a"
                  fill="var(--accent, #0f766e)"
                  name="Subscription"
                />
                <Bar
                  dataKey="usage"
                  stackId="a"
                  fill="var(--info, #3b82f6)"
                  name="Usage charges"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {(mom?.series?.length ?? 0) > 0 && (
          <Table
            columns={momColumns}
            data={[...(mom!.series)].reverse()}
            rowKey="month"
          />
        )}
        {(joinedLabel || freeUntilLabel) && (
          <Footnote>
            {joinedLabel ? <>Joined {joinedLabel}. </> : null}
            {freeUntilLabel
              ? `Free subscription months run through ${freeUntilLabel}.`
              : null}
          </Footnote>
        )}
      </PanelCard>
    </div>
  );
};

export default UsagePanel;
